"use client";

import { useEffect } from "react";
import { isIOS, isCapacitorApp } from "@/lib/utils";

/**
 * Component that prevents pinch-zoom and double-tap zoom on mobile devices
 * This makes the app behave like a native mobile app.
 *
 * CRITICAL: Skip on iOS Capacitor - the native WebView + viewport meta (maximumScale=1)
 * already prevent zoom. Our touch handlers with preventDefault can block scrolling
 * and button clicks on iOS, causing a completely unresponsive UI.
 */
export function PreventZoom() {
  useEffect(() => {
    // Skip on iOS Capacitor - touch handlers cause unresponsive UI (scroll/buttons broken)
    if (isCapacitorApp() && isIOS()) {
      return;
    }
    // Prevent pinch zoom (multiple touches)
    let hadTouchMove = false;
    const handleTouchStart = (event: TouchEvent) => {
      if (event.touches.length > 1) {
        event.preventDefault();
      } else {
        hadTouchMove = false; // Reset for new touch
      }
    };

    // Prevent gesture zoom (iOS Safari)
    const handleGestureStart = (e: Event) => {
      e.preventDefault();
    };

    // Prevent double-tap zoom (but NOT when user scrolled - that would block scroll on Android)
    let lastTouchEnd = 0;
    const handleTouchEnd = (event: TouchEvent) => {
      const now = Date.now();
      if (now - lastTouchEnd <= 300 && !hadTouchMove) {
        event.preventDefault();
      }
      lastTouchEnd = now;
      hadTouchMove = false;
    };

    // Prevent pinch zoom on touchmove (track that user scrolled so we don't block touchend)
    const handleTouchMove = (e: TouchEvent) => {
      if (e.touches.length > 1) {
        e.preventDefault();
      } else {
        hadTouchMove = true;
      }
    };

    // Add event listeners
    document.addEventListener("touchstart", handleTouchStart, { passive: false });
    document.addEventListener("gesturestart", handleGestureStart, { passive: false });
    document.addEventListener("touchend", handleTouchEnd, { passive: false });
    document.addEventListener("touchmove", handleTouchMove, { passive: false });

    // Cleanup
    return () => {
      document.removeEventListener("touchstart", handleTouchStart);
      document.removeEventListener("gesturestart", handleGestureStart);
      document.removeEventListener("touchend", handleTouchEnd);
      document.removeEventListener("touchmove", handleTouchMove);
    };
  }, []);

  return null; // This component doesn't render anything
}

